<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <!-- Encabezado -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-emerald-50/50 dark:bg-emerald-900/10">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Smartphone class="w-5 h-5 text-emerald-600" />
            Crear Instancia
          </h2>
          <p class="text-xs text-slate-500">Configura un nuevo entorno de WhatsApp.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Formulario -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-6">

        <div>
          <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">
            * Nombre de la Instancia (ID Único)
          </label>
          <input
            v-model="instanceName"
            type="text"
            class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-slate-700 dark:text-slate-200"
            placeholder="Ej: soporte_neurals_01"
            required
            pattern="[\w\-]+"
            title="Solo letras, números, guiones y guiones bajos"
          />
          <p class="text-[10px] text-slate-400 mt-1">
            Este nombre se usará para enlazar al Agente IA. No uses espacios.
          </p>
        </div>

        <div>
          <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">
            Integración
          </label>
          <select disabled class="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-500 cursor-not-allowed">
            <option>WHATSAPP-BAILEYS</option>
          </select>
        </div>

        <!-- Botones -->
        <div class="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
          <button @click="$emit('close')" type="button" class="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 transition-colors">
            Cancelar
          </button>
          <button type="submit" :disabled="isSaving || !instanceName" class="flex-1 px-4 py-2 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Crear
          </button>
        </div>
      </form>

    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { X, Save, Loader2, Smartphone } from 'lucide-vue-next';
import axios from 'axios';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';

const emit = defineEmits(['close', 'created']);
defineProps({ show: Boolean });

const { showSuccess, showError } = useToast();
const instanceName = ref('');
const isSaving = ref(false);

const EVO_URL = 'https://wapi.therealdevs.pro';
const EVO_KEY = 'TheRealDevsPro2026';

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const currentUser = SecureStorage.getItem('neurals_user');

    // 1. Crear la instancia en Evolution API
    await axios.post(
      `${EVO_URL}/instance/create`,
      {
        instanceName: instanceName.value.trim(),
        integration: 'WHATSAPP-BAILEYS',
        qrcode: true
      },
      { headers: { apikey: EVO_KEY } }
    );

    // 2. Guardar en tu base de datos mediante POST /instances
    const payload = {
      name: instanceName.value.trim(),
      owner: currentUser?.id || 1
    };

    await apiClient.post('/instances', payload);

    showSuccess('Instancia creada correctamente');
    instanceName.value = '';
    emit('created');
    emit('close');
  } catch (err) {
    const msg = err.response?.data?.response?.message?.[0] || err.response?.data?.message || 'Error al crear la instancia';
    showError(msg);
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
</style>